import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { RouteWPSProps } from './types';
import {
  ArcDroneMarker,
  ArcPolyline,
  DestinationMarker,
  DisabledMarker,
  DistanceLabel,
} from '@src/core/controllers';
import RouteWaypointLabel from '@src/features/order/pages/upsert-mission-page/components/route-waypoint-label';
import RouteEdgeInsertLabel from '@src/features/order/pages/upsert-mission-page/components/route-edge-insert-label';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { useDispatch, useSelector } from 'react-redux';
import { selectEditableRoute, selectWaypoints, setWaypoints } from '@src/features/order/pages/upsert-mission-page/redux';
import { SettingUnitType, useGetSettingByUnitType } from '@src/core/features';
import { arcConvertBetweenUnitTypes } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { getUnitType, useUnitType } from '@src/core/features/settings/units';
import { UnitType } from '@src/core/enums';

const RouteWPS: React.FC<RouteWPSProps> = (props) => {
  const { hideDestinationMarker = false, takeoffDrone = 'arrow' } = props;
  const dispatch = useDispatch();
  const wps = useSelector(selectWaypoints);
  const editableRoute = useSelector(selectEditableRoute);
  const hideDestination = hideDestinationMarker;
  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const unitType = useUnitType();
  // FLIGHT_ALTITUDE setting now arrives in SI (m); waypoint altitudes are display units.
  const flightAltitudeDisplay = React.useMemo(
    () => arcConvertBetweenUnitTypes(Number(flightAltitude.value), MeasurementType.DISTANCE, UnitType.SI, getUnitType()),
    [flightAltitude.value, unitType],
  );
  const [polylineInstance, setPolylineInstance] = React.useState<google.maps.Polyline | null>(null);

  const numberedWaypoints = React.useMemo(() => {
    const takeoff = wps.find((w) => w.type === RouteWaypointType.TAKE_OFF);
    const destination = wps.find((w) => w.type === RouteWaypointType.DESTINATION);
    const COINCIDENT_EPS = 1e-5; // ~1 m
    const coincides = (a: RouteWaypointEntity, b?: RouteWaypointEntity) =>
      !!b &&
      Math.abs(Number(a.latitude) - Number(b.latitude)) < COINCIDENT_EPS &&
      Math.abs(Number(a.longitude) - Number(b.longitude)) < COINCIDENT_EPS;

    let counter = 0;
    return wps
      .filter((w) => w.type === RouteWaypointType.WAYPOINT)
      .reduce<{ wp: RouteWaypointEntity; waypointNumber: number }[]>((acc, wp) => {
        if (coincides(wp, takeoff) || coincides(wp, destination)) return acc;
        counter += 1;
        acc.push({ wp, waypointNumber: counter });
        return acc;
      }, []);
  }, [wps]);

  const edgeMidpoints = React.useMemo(() => {
    const mids: google.maps.LatLngLiteral[] = [];
    for (let i = 0; i < wps.length - 1; i += 1) {
      const a = wps[i];
      const b = wps[i + 1];
      mids.push({
        lat: (Number(a.latitude) + Number(b.latitude)) / 2,
        lng: (Number(a.longitude) + Number(b.longitude)) / 2,
      });
    }
    return mids;
  }, [wps]);

  const initialPath = React.useMemo(
    () =>
      wps.map((w) => ({
        lat: Number(w.latitude),
        lng: Number(w.longitude),
      })),
    [wps],
  );

  const onSetAt = React.useCallback((idx: number) => {
    if (!polylineInstance) return;

    const path = polylineInstance.getPath();
    const coords = path.getAt(idx);

    const wpToUpdate = wps[idx];

    if (!wpToUpdate) return;

    if (wpToUpdate.type !== RouteWaypointType.WAYPOINT) {
      path.setAt(idx, new google.maps.LatLng(Number(wpToUpdate.latitude), Number(wpToUpdate.longitude)));
      return;
    }

    const updatedWp: RouteWaypointEntity = {
      ...wpToUpdate,
      latitude: coords.lat(),
      longitude: coords.lng(),
    };

    const newWps = [...wps];
    newWps[idx] = updatedWp;
    dispatch(setWaypoints(newWps));

  }, [polylineInstance, wps, dispatch]);


  const onInsertAt = React.useCallback((idx: number) => {
    if (!polylineInstance) return;

    const path = polylineInstance.getPath();
    const coords = path.getAt(idx);

    const wp: RouteWaypointEntity = {
      uuid: uuidv4(),
      latitude: coords.lat(),
      longitude: coords.lng(),
      type: RouteWaypointType.WAYPOINT,
      altitude: flightAltitudeDisplay,
    };

    const newWps = [...wps];
    newWps.splice(idx, 0, wp);
    dispatch(setWaypoints(newWps));
  }, [polylineInstance, wps, flightAltitudeDisplay]);


  React.useEffect(() => {
    if (!editableRoute || !polylineInstance) return;
    const path = polylineInstance.getPath();
    const listeners = [
      path.addListener('set_at', (idx: number) => onSetAt(idx)),
      path.addListener('insert_at', (idx: number) => onInsertAt(idx)),
    ];
    return () => listeners.forEach((l) => l.remove());
  }, [editableRoute, polylineInstance, onSetAt, onInsertAt]);

  React.useEffect(() => {
    if (!editableRoute || !polylineInstance) return;
    const map = polylineInstance.getMap();
    if (!map) return;
    const blockers = wps
      .filter((w) => w.type !== RouteWaypointType.WAYPOINT)
      .map(
        (w) =>
          new google.maps.Marker({
            position: { lat: Number(w.latitude), lng: Number(w.longitude) },
            map,
            clickable: true,
            draggable: false,
            cursor: 'default',
            optimized: false,
            zIndex: 3_000_000,
            icon: { path: google.maps.SymbolPath.CIRCLE, scale: 11, fillOpacity: 0, strokeOpacity: 0 },
          }),
      );
    return () => blockers.forEach((m) => m.setMap(null));
  }, [editableRoute, polylineInstance, wps]);

  const onLoad = React.useCallback((polyline: google.maps.Polyline) => {
    setPolylineInstance(polyline);
  }, []);

  const onUnmount = React.useCallback(() => {
    setPolylineInstance(null);
  }, []);

  const drone = wps.find((w) => w.type === RouteWaypointType.TAKE_OFF);
  const order = wps.find((w) => w.type === RouteWaypointType.DESTINATION);

  return (
    <React.Fragment>
      {!hideDestination ? (
        <DestinationMarker.confirmed
          position={{
            lat: Number(order?.latitude),
            lng: Number(order?.longitude),
          }}
        />
      ) : null}

      {takeoffDrone !== 'hidden' && drone ? (
        takeoffDrone === 'selected' ? (
          <ArcDroneMarker.idleSelected
            position={{ lat: Number(drone.latitude), lng: Number(drone.longitude) }}
          />
        ) : (
          <ArcDroneMarker.idleArrow
            position={{ lat: Number(drone.latitude), lng: Number(drone.longitude) }}
          />
        )
      ) : null}

      <ArcPolyline.future
        path={initialPath}
        options={{ editable: editableRoute }}
        onLoad={onLoad}
        onUnmount={onUnmount}
      />

      <DistanceLabel path={initialPath} />

      {editableRoute
        ? (() => {
          const seen = new Set<string>();
          return edgeMidpoints.map((mid, index) => {
            const key = `${mid.lat.toFixed(6)},${mid.lng.toFixed(6)}`;
            if (seen.has(key)) return null;
            seen.add(key);
            return <RouteEdgeInsertLabel key={`edge-${index}`} position={mid} />;
          });
        })()
        : null}

      {numberedWaypoints.map(({ wp, waypointNumber }) => (
        <RouteWaypointLabel
          key={wp.uuid}
          waypointNumber={waypointNumber}
          position={{
            lat: Number(wp.latitude),
            lng: Number(wp.longitude),
          }}
        />
      ))}

      {wps.map((w, idx) => {
        if (w.type === RouteWaypointType.WAYPOINT) return null;
        if (hideDestination && w.type === RouteWaypointType.DESTINATION) return null;
        return (
          <DisabledMarker
            key={idx}
            position={{
              lat: Number(w.latitude),
              lng: Number(w.longitude),
            }}
          />
        );
      })}
    </React.Fragment>
  );
};

export default RouteWPS;
